'server-only'

import { createAdminClient } from '@/lib/supabase/admin'

/**
 * Create a log entry in the logs table
 * @param type The type of log (e.g., 'STRIPE_QUEUE_PROCESS')
 * @param data The data to store as JSON
 * @returns The created log entry or null if there was an error
 */
export async function createLogAsAdmin(
    type: string,
    data: Record<string, any>
): Promise<any | null> {
    const supabase = await createAdminClient()
    
    const { data: logEntry, error } = await supabase
        .from('logs')
        .insert({
            type,
            data
        })
        .select('*')
        .single()
    
    if (error) {
        console.error(`Error creating log entry of type ${type}:`, error)
        return null
    }
    
    return logEntry
}

/**
 * Get logs of a specific type
 * @param type The type of logs to retrieve
 * @param limit Maximum number of logs to retrieve
 * @returns Array of log entries
 */
export async function getLogs(
    type: string,
    limit: number = 100
): Promise<any[]> {
    const supabase = await createAdminClient()
    
    const { data, error } = await supabase
        .from('logs')
        .select('*')
        .eq('type', type)
        .order('created_at', { ascending: false })
        .limit(limit)
    
    if (error) {
        console.error(`Error retrieving logs of type ${type}:`, error)
        return []
    }
    
    return data || []
}

/**
 * Logs a Stripe queue processing run
 * @param startTime When the processing started
 * @param endTime When the processing ended
 * @param processedEventIds Array of event IDs that were processed
 * @param status The final status of the processing run
 * @param metadata Additional metadata about the run
 * @returns The created log entry or null
 */
export async function logStripeQueueProcessAsAdmin(
    startTime: Date,
    endTime: Date,
    processedEventIds: string[],
    status: 'success' | 'partial_success' | 'error',
    metadata: Record<string, any> = {}
): Promise<any | null> {
    return createLogAsAdmin('STRIPE_QUEUE_PROCESS', {
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        duration_ms: endTime.getTime() - startTime.getTime(),
        processed_event_ids: processedEventIds,
        processed_count: processedEventIds.length,
        status,
        ...metadata
    })
} 